import { createContext, useContext } from 'react';

import type { DateFormat, DisplayMode, TimeFormat } from '@treneo/shared/constants';
import { formatDateTimeWithFormat, formatDateWithFormat } from '@treneo/shared/utils';

export interface SharedDateTimeConfig {
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  displayMode: DisplayMode;
  timeStep: number;
  mondayFirst: boolean;
}

export const DateTimeConfigContext = createContext<SharedDateTimeConfig | null>(null);

export const useDateTimeConfig = () => {
  const context = useContext(DateTimeConfigContext);
  if (!context) {
    throw new Error('useDateTimeConfig must be used within a DateTimeConfigProvider');
  }

  return context;
};

export type FormatDateFn = (date: string | Date | null | undefined, fallback?: string) => string;
export type FormatDateTimeFn = (
  date: string | Date | null | undefined,
  fallback?: string
) => string;

export const useFormatDateTime = (): {
  formatDate: FormatDateFn;
  formatDateTime: FormatDateTimeFn;
} => {
  const { dateFormat, timeFormat } = useDateTimeConfig();

  const formatDate: FormatDateFn = (date, fallback = '') =>
    formatDateWithFormat(date, dateFormat, fallback);

  const formatDateTime: FormatDateTimeFn = (date, fallback = '') =>
    formatDateTimeWithFormat(date, dateFormat, timeFormat, fallback);

  return { formatDate, formatDateTime };
};
